/*
  selectTheme.js  (hosted payment page, src/utils/selectTheme.js)
  ---------------------------------------------------------------
  react-select renders its own DOM with emotion-hashed class names, so global
  CSS / module CSS cannot reliably theme it. Pass these styles to every
  <Select> on the hosted page instead:

      import { wqSelectStyles } from "../../utils/selectTheme";
      <Select styles={wqSelectStyles()} ... />

  Returns {} in light mode (react-select keeps its defaults), and a dark style
  set when the embedding app passed theme=dark. Theme is read synchronously
  from the URL/sessionStorage/<html> attribute, so it is correct on first paint.
*/

export const getWqTheme = () => {
    try {
      const p = new URLSearchParams(window.location.search).get("theme");
      if (p === "dark" || p === "light") return p;
      const ss = sessionStorage.getItem("wq_theme");
      if (ss === "dark" || ss === "light") return ss;
      const attr = document.documentElement.getAttribute("data-wq-theme");
      if (attr === "dark" || attr === "light") return attr;
    } catch (e) {
      // window/document not available: fall through
    }
    return "light";
  };
  
  const DARK = {
    surface: "#1b1f27",
    elevated: "#232833",
    text: "#e5e7eb",
    muted: "#9ca3af",
    border: "#374151",
    accent: "#FF6700",
    optionHover: "#2a2f3a",
    optionActive: "#33394a",
  };
  
  export const wqSelectStyles = () => {
    if (getWqTheme() !== "dark") return {}; // light: leave react-select defaults
  
    return {
      control: (base, state) => ({
        ...base,
        backgroundColor: DARK.surface,
        borderColor: state.isFocused ? DARK.accent : DARK.border,
        boxShadow: state.isFocused ? `0 0 0 1px ${DARK.accent}` : "none",
        ":hover": { borderColor: DARK.accent },
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: DARK.elevated,
        border: `1px solid ${DARK.border}`,
        zIndex: 30,
      }),
      menuList: (base) => ({ ...base, backgroundColor: DARK.elevated }),
      option: (base, state) => ({
        ...base,
        color: DARK.text,
        backgroundColor: state.isSelected
          ? DARK.optionActive
          : state.isFocused
          ? DARK.optionHover
          : DARK.elevated,
        ":active": { backgroundColor: DARK.optionActive },
      }),
      singleValue: (base) => ({ ...base, color: DARK.text }),
      input: (base) => ({ ...base, color: DARK.text }),
      placeholder: (base) => ({ ...base, color: DARK.muted }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: DARK.border }),
      dropdownIndicator: (base, state) => ({
        ...base,
        color: state.isFocused ? DARK.accent : DARK.muted,
        ":hover": { color: DARK.accent },
      }),
      clearIndicator: (base) => ({ ...base, color: DARK.muted }),
      noOptionsMessage: (base) => ({ ...base, color: DARK.muted }),
      loadingMessage: (base) => ({ ...base, color: DARK.muted }),
    };
  };